import EventEmitter2 from "eventemitter2";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

import FavoriteRestaurant from "@core/favoriteRestaurant";
import GeoLocation from "@core/geolocation";
import { $, $$ } from "@utils/dom";
import { on, off } from "@utils/listener";
import Viewport from "@utils/viewport";

const SORTED_CLASSNAME = "--js-sorted";
const ZOOM_LEVELS = {
  default: 8,
  precise: 12,
};
const DEFAULT_MAP_OPTIONS = {
  center: { lat: 45.5029925, lng: -73.6963375 }, // Montreal
  fullscreenControl: false,
  gestureHandling: 'cooperative',
  keyboardShortcuts: false,
  mapId: 'SCORES.CA',
  mapTypeControl: false,
  maxZoom: 20,
  minZoom: 6,
  rotateControl: false,
  scaleControl: false,
  scrollwheel: false,
  streetViewControl: false,
  zoom: ZOOM_LEVELS.default,
};


async function loadAPI(callback) {
  if( !google.maps.Map || !google.maps.InfoWindow ) await google.maps.importLibrary("maps");
  if( !google.maps.marker ) await google.maps.importLibrary("marker");
  if( !google.maps.geometry ) await google.maps.importLibrary("geometry");
  if( !google.maps.Geocoder ) await google.maps.importLibrary("geocoding");

  callback();
};

class ArchiveRestaurants {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;
    this.form = $('.archive-restaurants__form', this.el);
    this.search = $('.archive-restaurants__search', this.el);
    this.grid = $('.archive-restaurants__grid', this.el);
    this.subtitles = [ ...$$('.archive-restaurants__subtitle', this.el) ];
    this.map = $('.archive-restaurants__map', this.el);
    this.markerIcon = $('.archive-restaurants__marker', this.el);
    this.positionIcon = $('.archive-restaurants__position', this.el);
    this.clusterIcon = $('.archive-restaurants__cluster', this.el);
    this.list = $('.archive-restaurants__posts', this.el);
    this.restaurants = [ ...$$('.archive-restaurants__restaurant', this.el) ].map(restaurant => new Restaurant(restaurant));

    this.apiLoaded = false;
    this.center = null;
    this.clusterer = null;
    this.geocoder = null;
    this.gmap = null;
    this.pin = null;
    this.infoWindow = null;

    this._onMarkerClick = this._onMarkerClick.bind(this);
    this._onRestaurantOpen = this._onRestaurantOpen.bind(this);
    this._onGeoLocationChange = this._onGeoLocationChange.bind(this);
    this._onFavoriteRestaurantChange = this._onFavoriteRestaurantChange.bind(this);
    this._onFormSubmit = this._onFormSubmit.bind(this);
    this._onGeocodeResults = this._onGeocodeResults.bind(this);
  }

  init() {
    if( GeoLocation.latitude && GeoLocation.longitude ) {
      this.center = GeoLocation.position;
      this.el.classList.add(SORTED_CLASSNAME);

      this._bindEvents();
      loadAPI( this._onAPILoaded.bind(this) );
    }
    else {
      this._bindEvents();
      loadAPI( this._onAPILoaded.bind(this) );

      GeoLocation.request();
    }
  }
  destroy() {
    if( this.restaurants ) this.restaurants.forEach(restaurant => restaurant.destroy());
    if( this.infoWindow ) this.infoWindow.unbindAll();
    if( this.gmap ) this.gmap.unbindAll();

    this.el = null;
    this.emitter = null;
    this.form = null;
    this.search = null;
    this.grid = null;
    this.subtitles = null;
    this.map = null;
    this.markerIcon = null;
    this.positionIcon = null;
    this.clusterIcon = null;
    this.list = null;
    this.restaurants = null;

    this.center = null;
    this.clusterer = null;
    this.geocoder = null;
    this.gmap = null;
    this.pin = null;
    this.infoWindow = null;

    this._onMarkerClick = null;
    this._onRestaurantOpen = null;
    this._onGeoLocationChange = null;
    this._onFavoriteRestaurantChange = null;
    this._onFormSubmit = null;
    this._onGeocodeResults = null;
  }
  stop() {
    this._unbindEvents();
  }

  _bindEvents() {
    GeoLocation.on('change', this._onGeoLocationChange);
    FavoriteRestaurant.on('change', this._onFavoriteRestaurantChange);

    if( this.form ) on(this.form, 'submit', this._onFormSubmit);
    if( this.restaurants ) this.restaurants.forEach(restaurant => restaurant.on('open', this._onRestaurantOpen));
  }
  _unbindEvents() {
    GeoLocation.off('change', this._onGeoLocationChange);
    FavoriteRestaurant.off('change', this._onFavoriteRestaurantChange);

    if( this.form ) off(this.form, 'submit', this._onFormSubmit);
    if( this.restaurants ) this.restaurants.forEach(restaurant => restaurant.off('open', this._onRestaurantOpen));
  }
  _sortRestaurants(position) {
    // bail early if Google Maps API isn't loaded
    if( !this.apiLoaded ) return;

    if( position ) {
      // calculate distance of all restaurants from center
      this.restaurants.forEach(restaurant => { restaurant.distance = google.maps.geometry.spherical.computeDistanceBetween(position, restaurant.position); });

      // sort by distance
      this.restaurants.sort((a, b) => a.distance - b.distance);
    }

    // update DOM
    this.restaurants.forEach(restaurant => { this.list.appendChild(restaurant.el); });
    if( position ) this.el.classList.add(SORTED_CLASSNAME);

    // update subtitles visibility
    this.subtitles.forEach((subtitle, index) => {
      subtitle.setAttribute('aria-hidden', index === 0);
    });

    // open first restaurant
    this.restaurants[0].open();
  }

  _onAPILoaded() {
    // save Google Maps API loaded status
    this.apiLoaded = true;

    // bail early if module as been destroyed since
    if( !this.map ) return;

    const options = {};

    // set Map ID from [data-map-id] if exists
    if( this.el.hasAttribute('data-map-id') ) options.mapId = this.el.dataset.mapId;

    // set map center from user position if available OR from [data-map-center] if exists
    if( this.center ) {
      options.center = this.center;
      options.zoom = ZOOM_LEVELS.precise;
    }
    else if( this.el.hasAttribute('data-map-center') ) {
      const [ lat, lng ] = this.el.dataset.mapCenter.split(',');
      if( lat && lng ) options.center = { lat: parseFloat(lat), lng: parseFloat(lng) };
    }

    this.gmap = new google.maps.Map(this.map, { ...DEFAULT_MAP_OPTIONS, ...options });
    this.infoWindow = new google.maps.InfoWindow({ maxWidth: 650, /*disableAutoPan: true*/ });

    // create markers
    const markers = this.restaurants.map(restaurant => {
      const { position } = restaurant;
      if( !position ) return;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.gmap,
        position: position,
        content: this.markerIcon.cloneNode(true),
        title: restaurant.name,
        gmpClickable: true,
      });

      restaurant.marker = marker;
      restaurant.on('marker', this._onMarkerClick);

      return marker;
    });

    // your position marker
    this.pin = new google.maps.marker.AdvancedMarkerElement({
      map: this.center ? this.gmap : null,
      position: this.center ? this.center : null,
      gmpClickable: false,
      content: this.positionIcon,
    });

    // create marker clusterer
    this.clusterer = new MarkerClusterer({
      markers: markers,
      map: this.gmap,
      renderer: {
        render: ({ count, position }) => {
          const icon = this.clusterIcon.cloneNode(true);
          $('text', icon).textContent = count;

          return new google.maps.marker.AdvancedMarkerElement({
            position,
            //content: new google.maps.marker.PinElement({ glyph: label, glyphColor: "white" }).element,
            content: icon,
            //zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          });
        }
      }
    });

    // sort restaurants by distance from Map's center
    if( this.center ) this._sortRestaurants(this.center);
  }
  _onMarkerClick(restaurant) {
    this.infoWindow.setHeaderContent(restaurant.name);
    this.infoWindow.setContent(restaurant.address);
    this.infoWindow.open(this.gmap, restaurant.marker);

    restaurant.open();
    this.emitter.emit('SiteScroll.scrollTo', restaurant.el, { offset: -112 });
  }
  _onRestaurantOpen(restaurant) {
    // close all other restaurants
    this.restaurants.forEach(r => {
      if( r !== restaurant ) r.close();
    });

    // move map to this restaurant
    if( this.gmap ) {
      this.gmap.panTo(restaurant.position);
    }

    // update site-scroll
    this.emitter.emit('SiteScroll.update');
  }
  _onGeoLocationChange() {
    // bail early if module as been destroyed since
    if( !this.map ) return;

    // save center
    this.center = GeoLocation.position;

    // bail early if Google Maps API isn't loaded
    if( !this.apiLoaded ) return;

    // move Map to center & zoom
    if( this.gmap ) {
      this.gmap.setCenter(this.center);
      this.gmap.setZoom(ZOOM_LEVELS.precise);
    }

    // show your current position on Map
    if( this.pin ) {
      this.pin.position = this.center;
      this.pin.setMap(this.gmap);
    }

    // sort restaurants by distance from Map's center
    this._sortRestaurants(this.center);
  }
  _onFavoriteRestaurantChange() {
    this.restaurants.forEach(restaurant => restaurant.favorite = restaurant.slug === FavoriteRestaurant.slug);

    // update site-scroll
    this.emitter.emit('SiteScroll.update');
  }
  _onFormSubmit(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    // bail early if value is empty or smaller than 3 characters
    const value = this.search.value.trim();
    if( !value || value.length < 3 ) return;

    // create Geocoder if doesn't exist
    if( !this.geocoder ) this.geocoder = new google.maps.Geocoder();

    // get latitude/longitude for this address
    this.geocoder.geocode({
      address: value,
      fulfillOnZeroResults: true,
      language: MILL3WP.locale,
      region: 'CA'
    }, this._onGeocodeResults);

    let offset = -66;

    if( Viewport.width >= 1200 ) offset = -100;
    else if( Viewport.width >= 768 ) offset = -72;

    this.emitter.emit('SiteScroll.scrollTo', this.grid, { smooth: true, offset });
  }
  _onGeocodeResults(results, status) {
    // bail early if module has been destroyed during request
    if( !this.gmap ) return;

    if( status === google.maps.GeocoderStatus.OK ) {
      const { location } = results[0].geometry;

      this.gmap.panTo(location);
      this.gmap.setZoom(ZOOM_LEVELS.precise);
      this._sortRestaurants(location);
    } else {
      console.log(status);
      console.log(results);

      /*
      switch( status ) {
        case google.maps.GeocoderStatus.ERROR: break;
        case google.maps.GeocoderStatus.INVALID_REQUEST: break;
        case google.maps.GeocoderStatus.OVER_QUERY_LIMIT: break;
        case google.maps.GeocoderStatus.REQUEST_DENIED: break;
        case google.maps.GeocoderStatus.UNKNOWN_ERROR: break;
        case google.maps.GeocoderStatus.ZERO_RESULTS: break;
      }
      */
    }
  }
}

class Restaurant extends EventEmitter2 {
  constructor(el) {
    super();

    this.el = el;
    this.name = $('.restaurant-preview__title', this.el).innerText;
    this.address = $('.restaurant-preview__address', this.el).innerHTML;
    this.distanceEl = $('.restaurant-preview__distance', this.el);
    this.favoriteBtn = $('.restaurant-preview__favoriteBtn', this.el);
    this.nav = $('.restaurant-preview__nav', this.el);
    this.link = $('.restaurant-preview__link', this.el);
    this.orderLink = $('.restaurant-preview__order', this.el);
    this.id = this.el.dataset.id;
    this.slug = this.el.dataset.slug;
    this.latitude = this.el.dataset.latitude;
    this.longitude = this.el.dataset.longitude;

    if( this.id ) this.id = parseInt(this.id);
    if( this.latitude ) this.latitude = parseFloat(this.latitude);
    if( this.longitude ) this.longitude = parseFloat(this.longitude);

    this.position = this.latitude && this.longitude ? { lat: this.latitude, lng: this.longitude } : null;

    this._distance = null;
    this._favorite = null;
    this._listener = null;
    this._marker = null;
    this._opened = false;

    this._onMarkerClick = this._onMarkerClick.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onFavoriteBtnClick = this._onFavoriteBtnClick.bind(this);

    this.init();
  }

  init() {
    if( FavoriteRestaurant.slug === this.slug ) this.favorite = true;

    this._bindEvents();
  }
  destroy() {
    this._unbindEvents();

    if( this._listener ) this._listener.remove();

    this.el = null;
    this.name = null;
    this.address = null;
    this.distanceEl = null;
    this.favoriteBtn = null;
    this.nav = null;
    this.link = null;
    this.orderLink = null;
    this.id = null;
    this.latitude = null;
    this.longitude = null;
    this.position = null;

    this._distance = null;
    this._favorite = null;
    this._listener = null;
    this._marker = null;
    this._opened = null;

    this._onMarkerClick = null;
    this._onClick = null;
    this._onFavoriteBtnClick = null;
  }
  open() {
    if( this._opened ) return;
    this._opened = true;

    if( this.favoriteBtn ) this.favoriteBtn.removeAttribute('inert');
    if( this.nav ) this.nav.removeAttribute('inert');

    this.el.classList.add('--js-open');
    this.emit('open', this);
  }
  close() {
    if( !this._opened ) return;
    this._opened = false;

    if( this.favoriteBtn ) this.favoriteBtn.setAttribute('inert', true);
    if( this.nav ) this.nav.setAttribute('inert', true);

    this.el.classList.remove('--js-open');
    this.emit('close', this);
  }

  _bindEvents() {
    on(this.el, 'click', this._onClick);
    if( this.favoriteBtn ) on(this.favoriteBtn, 'click', this._onFavoriteBtnClick);
  }
  _unbindEvents() {
    off(this.el, 'click', this._onClick);
    if( this.favoriteBtn ) off(this.favoriteBtn, 'click', this._onFavoriteBtnClick);
  }

  _onMarkerClick() { this.emit('marker', this); }
  _onClick(event) {
    if( event.srcElement === this.link || event.srcElement === this.orderLink ) return;
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if( this._opened ) this.close();
    else this.open();
  }
  _onFavoriteBtnClick(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    FavoriteRestaurant.slug = this.slug;
  }

  // getter - setter
  get distance() { return this._distance; }
  set distance(value) {
    if( this._distance === value ) return;

    this._distance = value;
    this.distanceEl.innerText = `${(this._distance / 1000).toFixed(1)} km`;
    this.distanceEl.removeAttribute('aria-hidden');
  }

  get favorite() { return this._favorite; }
  set favorite(value) {
    if( this._favorite === value ) return;

    this._favorite = value;
    this.favoriteBtn.setAttribute('aria-checked', this._favorite);
  }

  get marker() { return this._marker; }
  set marker(value) {
    if( value === this._marker ) return;

    if( this._listener ) this._listener.remove();

    this._listener = null;
    this._marker = value;

    if( this._marker ) this._listener = this._marker.addListener('gmp-click', this._onMarkerClick);
  }
}

export default ArchiveRestaurants;
